import { themeColors, themeMode } from '@juvare/common-web-ui/JuvareTheme';
import { Editor } from '@juvare/common-web-ui/tinymce-react';
import { Box, CircularProgress, useTheme } from '@mui/material';
import React from 'react';
import { humanFileSize } from '../WidgetFormFile/Components/utils';
// when updating tinymce, check that code within init_instance_callback block still works
const defaultTinymceScriptSrc = 'https://cdnjs.juvare.com/ajax/libs/tinymce/6.8.6-tinymce-6.8.8/tinymce.min.js';
const initialEditorHeight = 218;
export const defaultContentSizeLimit = 1000; // in KB
export const defaultImageSizeLimit = 200; // in KB
/**
 * RichTextEditor is a wrapper around TinyMCE for rich text editing.
 * It includes custom configuration options, size limits, and image upload handling.
 *
 * @component
 *
 * @param {RichTextEditorProps} props - The props for RichTextEditor.
 * @param {string} props.data - Editor content as a string (HTML format).
 * @param {TinyMceEditorProps['init']} [props.editorInit] - Optional TinyMCE init configuration.
 * @param {boolean} [props.disabled] - Disables the editor if true.
 * @param {(htmlMessage: string) => void} props.onEditorChange - Callback triggered when editor content changes.
 * @param {string} [props.tinymceScriptSrc] - Optional custom TinyMCE script source path.
 * @param {number} [props.contentSizeLimit=1000] - Maximum allowed content size in KB. Default is 1000 KB.
 * @param {number} [props.imageSizeLimit=200] - Maximum allowed image size in KB. Default is 200 KB.
 *
 * @returns {React.ReactElement} Rendered rich text editor component.
 */
export const RichTextEditor = (props) => {
    var _a, _b;
    const divRef = React.useRef(null);
    const editorInitUpdate = (props === null || props === void 0 ? void 0 : props.editorInit) || {};
    const [loading, setLoading] = React.useState(true);
    const theme = useTheme();
    const contentSizeLimit = ((_a = props.contentSizeLimit) !== null && _a !== void 0 ? _a : defaultContentSizeLimit) * 1024; // in KB
    const imageSizeLimit = ((_b = props.imageSizeLimit) !== null && _b !== void 0 ? _b : defaultImageSizeLimit) * 1024; // in KB
    const customImageUploadHandler = (blobInfo) => new Promise((resolve, reject) => {
        const file = blobInfo.blob();
        // In case which the max file size is 200Kb
        if (blobInfo.blob().size > imageSizeLimit) {
            return reject({ message: `Image size exceeds ${humanFileSize(imageSizeLimit)}`, remove: true });
        }
        // Proceed with image upload logic
        const reader = new FileReader();
        reader.onload = () => {
            let result = reader.result;
            if (!result) {
                reject({ message: 'Failed to read image file', remove: true });
                return;
            }
            // If result is an ArrayBuffer, convert it to Base64
            if (result instanceof ArrayBuffer) {
                const base64String = btoa(new Uint8Array(result).reduce((data, byte) => data + String.fromCharCode(byte), ''));
                result = `data:${file.type};base64,${base64String}`;
            }
            resolve(result);
        };
        reader.onerror = () => {
            reject({ message: 'Failed to read image file', remove: true });
        };
        reader.readAsDataURL(file); // Convert to base64 URL
    });
    return (React.createElement(Box, { "data-test": 'rich-text-editor-container', ref: divRef, sx: loading ? { position: 'relative', height: initialEditorHeight, '& textarea': { display: 'none' } } : undefined },
        loading && (React.createElement(Box, { style: {
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
            } },
            React.createElement(CircularProgress, null))),
        React.createElement(Editor, { value: props.data, 
            // TODO: fix tinymce types
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onEditorChange: (value, Editor) => {
                const currentEditorSize = new Blob([value]).size;
                if (currentEditorSize > contentSizeLimit) {
                    Editor.notificationManager.open({
                        type: 'error',
                        text: `Editor size limit is exceeded. Limit is ${humanFileSize(contentSizeLimit)}, and current editor size is ${humanFileSize(currentEditorSize)}`,
                    });
                }
                else {
                    props.onEditorChange(value);
                }
            }, disabled: props.disabled, onInit: () => setLoading(false), init: Object.assign({ init_instance_callback: () => {
                    // This code puts tinymce aux div used to display dialogs instead of body but as sibling to the editor component
                    // this solves issue where tinymce popups lose focus when displayed inside material dialog
                    if (divRef.current) {
                        const auxElements = document.querySelectorAll('body > .tox-tinymce-aux');
                        if (auxElements.length) {
                            divRef.current.append(auxElements[auxElements.length - 1]);
                        }
                        else {
                            console.warn('div with class .tox-tinymce-aux was not found please check the init_instance_callback code');
                        }
                    }
                }, height: initialEditorHeight, menubar: false, statusbar: true, resize: true, elementpath: false, branding: false, plugins: 'image table lists link', toolbar: 'undo redo | ' +
                    'bold italic underline strikethrough | ' +
                    'fontfamily fontsize blocks | ' +
                    'alignleft aligncenter alignright alignjustify | ' +
                    'outdent indent |  ' +
                    'numlist bullist | ' +
                    'forecolor backcolor removeformat | ' +
                    'link | image table | addPlaceholderButton addAttachmentButton', toolbar_mode: 'sliding', skin: themeMode === 'light' ? 'oxide' : 'oxide-dark', content_css: themeMode === 'light' ? 'default' : 'dark', content_style: `body { font-family: helvetica, arial, sans-serif; font-size: ${theme.typography.body1.fontSize}px; background-color: ${themeColors.surface.primary}; color: ${themeColors.text.main} } .mce-content-readonly { color: ${themeColors.text.disable}; }`, images_upload_handler: customImageUploadHandler, urlconverter_callback: (url) => {
                    // Just return your raw URL, prevent TinyMCE from rewriting
                    return url;
                } }, editorInitUpdate), tinymceScriptSrc: (props === null || props === void 0 ? void 0 : props.tinymceScriptSrc) || defaultTinymceScriptSrc })));
};
